import { Info, Link2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TPipelineView } from "@/models/pipeline-views.model";
import { usePipelinesStore } from "@/store/pipelines/pipelines.store";

type Props = {
  view: TPipelineView;
  viewStageId: number;
  onChange: (viewStageId: number) => void;
};

/**
 * Replaces Pipeline + Status when a deal is added from a pipeline view (R7):
 * the deal lands in the chosen column and is created at its linked real stage,
 * or at the base pipeline's first stage for a view-only column.
 */
const ViewColumnField: React.FC<Props> = ({ view, viewStageId, onChange }) => {
  const firstBaseStage = usePipelinesStore((s) => s.entities[view.basePipelineId]?.status?.[0]);
  const column = view.stages.find((s) => s.id === viewStageId);

  return (
    <div className="space-y-2.5">
      <div>
        <Label className="mb-2 block text-[10.5px] font-medium uppercase tracking-[0.12em] text-slate-500">
          Column on {view.name} <span className="text-accent-red">*</span>
        </Label>
        <Select value={String(viewStageId)} onValueChange={(v) => onChange(Number(v))}>
          <SelectTrigger className="h-9 rounded-lg border-white/[0.08] bg-white/[0.02] text-[12.5px] text-slate-200">
            <SelectValue placeholder="Choose column" />
          </SelectTrigger>
          <SelectContent className="rounded-xl border-white/[0.08] bg-ink-800/95 text-slate-200 shadow-card backdrop-blur-xl">
            <SelectGroup>
              {view.stages.map((stage) => (
                <SelectItem key={stage.id} value={String(stage.id)} className="text-[12.5px]">
                  <span className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: stage.color }} />
                    {stage.name}
                    <span className="text-[10.5px] text-slate-500">
                      · {stage.linkedStatusId != null ? stage.linkedStatusName : "view only"}
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {column && (
        <div className="flex items-start gap-2 rounded-xl border border-brand-500/20 bg-brand-500/[0.06] px-3 py-2.5 text-[11px] leading-relaxed text-slate-300">
          {column.linkedStatusId != null ? (
            <>
              <Link2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-300" />
              <span>
                Created at <b className="text-white">{column.linkedStatusName}</b> on {view.basePipelineName}, and
                shown in {column.name}.
              </span>
            </>
          ) : (
            <>
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-300" />
              <span>
                {column.name} is view only: the deal is created at{" "}
                <b className="text-white">{firstBaseStage?.name ?? "the first stage"}</b>, the first stage of{" "}
                {view.basePipelineName}, and shown in {column.name}.
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewColumnField;
